import React, { useState } from "react";
import { CircularProgress, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import "./AgencyEmployeeStatusMenu.scss";
import EmployeeStatusConfirmModal, {
  ConfirmType,
  getConfirmConfig,
} from "./EmployeeStatusConfirmModal";

const AgencyEmployeeStatusMenu = ({
  status,
  onUpdateStatus,
}: {
  status: string;
  onUpdateStatus: (
    nextStatus: "active" | "deactivated" | "archived",
  ) => Promise<void>;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmType, setConfirmType] = useState<ConfirmType | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const open = Boolean(anchorEl);
  const normalized = (status || "").toLowerCase();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const activateLabel = normalized === "active" ? "Deactivate" : "Activate";
  const archiveLabel = normalized === "archived" ? "Unarchive" : "Archive";

  const openConfirmForActiveToggle = () => {
    const type: ConfirmType =
      normalized === "active" ? "deactivate" : "activate";
    setConfirmType(type);
    setConfirmOpen(true);
    handleClose();
  };

  const openConfirmForArchiveToggle = () => {
    const type: ConfirmType =
      normalized === "archived" ? "unarchive" : "archive";
    setConfirmType(type);
    setConfirmOpen(true);
    handleClose();
  };

  const closeConfirm = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmType(null);
  };

  const handleConfirm = async () => {
    if (!confirmType) return;

    const cfg = getConfirmConfig(confirmType);
    setConfirmLoading(true);
    setIsLoading(true);

    try {
      await onUpdateStatus(cfg.nextStatus as any);
      closeConfirm();
    } finally {
      setConfirmLoading(false);
      setIsLoading(false);
    }
  };

  return (
    <>
      <div
        className="property-menu-icon"
        onClick={handleClick}
        id="employee-action-btn"
        aria-controls={open ? "employee-action-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
      >
        {isLoading ? (
          <CircularProgress
            style={{ width: 15, height: 15 }}
            className="circular-progress-action-icon"
          />
        ) : (
          <MoreVertIcon fontSize="small" />
        )}
      </div>

      <Menu
        id="employee-action-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ "aria-labelledby": "employee-action-btn" }}
        className="users-list-menu-icon-menu"
        autoFocus={false}
        PaperProps={{
          elevation: 3,
          sx: { mt: 1, borderRadius: 2, minWidth: 160 },
        }}
      >
        <MenuItem onClick={openConfirmForActiveToggle}>
          {activateLabel}
        </MenuItem>
        <MenuItem onClick={openConfirmForArchiveToggle}>
          {archiveLabel}
        </MenuItem>
      </Menu>

      <EmployeeStatusConfirmModal
        open={confirmOpen}
        type={confirmType}
        loading={confirmLoading}
        onCancel={closeConfirm}
        onConfirm={handleConfirm}
      />
    </>
  );
};

export default AgencyEmployeeStatusMenu;